/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import Compressor from 'compressorjs'
import { useMutation, useQuery } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { Button, ButtonVariant } from '../../common/Button'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { IconChoices } from '../../common/Icon'
import { ToastType } from '../../common/Toast/toast-type'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { UserAvatar } from '../../common/Avatar'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { Label } from '../../common/Label'
import { convertBase64 } from '../../../api/helpers'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { useModal } from '../../../hooks/useModal'
import { Spinner } from '../../common/Spinner'
import { DeleteAccountModal } from './DeleteAccountModal'
import { ChangePassword } from '../ChangePassword'
import { ChangeEmail } from '../ChangeEmail'
import { isImageType } from '../../../helpers/types'
import { useAuth } from '../../../hooks/useAuth'
import { patchUser, userQueries } from '../../../api/users'
import { createMedia } from '../../../api/media'

export const ProfileSettings = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const isMobile = useIsMobile()
  const {
    show: showDeleteProfilePictureModal,
    open: openDeleteProfilePictureModal,
    close: closeDeleteProfilePictureModal,
  } = useModal()
  const {
    show: showDeleteProfileModal,
    open: openDeleteProfileModal,
    close: closeDeleteProfileModal,
  } = useModal()

  const { userId } = useAuth()
  const { data: user } = useQuery(userQueries.getUser(userId))

  const showErrorMessage = (message: string): void => {
    setToastNotification(ToastType.DANGER, message)
  }

  const showSuccessMessage = (message: string): void => {
    setToastNotification(ToastType.SUCCESS, message)
  }

  const { mutate: mutateUpdateProfilePicture, isPending: isUploadingPicture } =
    useMutation({
      mutationFn: async ({ base64 }: { base64: string }) => {
        if (!userId) throw new Error('No userId')
        const mediaId = (await createMedia('image', base64)).data.data?._id
        if (!mediaId) {
          return false
        }
        await patchUser({
          id: userId,
          body: {
            avatarId: mediaId,
          },
        })
        return true
      },
      onSuccess: (data) => {
        if (data) {
          showSuccessMessage(
            translate(
              'profileSettings.flashMessage.updateProfilePictureSuccess'
            )
          )
          return
        }
        showErrorMessage(
          translate('profileSettings.flashMessage.updateProfilePictureFailure')
        )
        captureException(new Error("Couldn't create media"))
      },
      onError: (error) => {
        showErrorMessage(translate('flashMessage.somethingWentWrongError'))
        captureException(error)
      },
    })
  const uploadPicture = async (base64: string): Promise<void> => {
    mutateUpdateProfilePicture({ base64 })
  }

  const { mutate: mutateRemoveProfilePicture, isPending: isRemovingPicture } =
    useMutation({
      mutationFn: async () => {
        if (!userId) throw new Error('no userId')
        return patchUser({
          id: userId,
          body: {
            avatarId: null,
          },
        })
      },
      onSuccess: () => {
        showSuccessMessage(
          translate('profileSettings.flashMessage.deleteProfilePictureSuccess')
        )
      },
      onError: (error) => {
        showErrorMessage(
          translate('profileSettings.flashMessage.deleteProfilePictureFailure')
        )
        captureException(error)
      },
    })

  const deleteProfilePicture = async (): Promise<void> => {
    mutateRemoveProfilePicture()
  }

  const onUploadImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) {
      return
    }
    const images = Array.from(e.target.files)
    if (images.length === 0) {
      return
    }
    const image = images[0]
    if (!isImageType(image.type)) {
      setToastNotification(
        ToastType.DANGER,
        translate('uploadImage.invalidImageType')
      )
      return
    }
    // eslint-disable-next-line no-new
    new Compressor(image, {
      quality: 0.8,
      success: async (result) => {
        const base64 = await convertBase64(result)
        await uploadPicture(base64 as string)
      },
    })
  }

  return (
    <div className="space-y-10">
      <HorizontalFormSection
        title={translate('profileSettings.profilePictureTitle')}
        icon={IconChoices.IMAGE}
        iconPlacement="left"
        borderBottom
      >
        <div className="flex items-center gap-5">
          <div className="rounded-full p-1 shadow">
            <UserAvatar size={isMobile ? 80 : 90} user={user} />
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              <div
                className="flex cursor-pointer items-center
                rounded border border border-solid border-neutral-80
                bg-white px-4 py-3 text-black hover:bg-neutral-96"
              >
                {isUploadingPicture || isRemovingPicture ? (
                  <Spinner />
                ) : (
                  <>
                    <Label
                      className="cursor-pointer"
                      name="profile-image-input"
                      label={translate(
                        'profileSettings.labels.updateProfilePicture'
                      )}
                    />
                    <input
                      id="profile-image-input"
                      type="file"
                      accept="image/png, image/jpg, image/jpeg"
                      className="hidden cursor-pointer"
                      onChange={onUploadImage}
                    />
                  </>
                )}
              </div>
              {user?.avatarId && (
                <Button
                  variant={ButtonVariant.TEXT}
                  onClick={openDeleteProfilePictureModal}
                  icon={IconChoices.DELETE_TRASH}
                />
              )}
            </div>
          </div>
        </div>
      </HorizontalFormSection>
      <ChangeEmail />
      <ChangePassword />
      <HorizontalFormSection
        title={translate('profileSettings.deleteAccount.title')}
        iconPlacement="left"
        subtitle={translate('profileSettings.deleteAccount.text')}
        icon={IconChoices.WARNING_AMBER}
      >
        <div className="flex justify-end">
          <Button
            label={translate('common.actions.delete')}
            onClick={openDeleteProfileModal}
            variant={ButtonVariant.DANGER}
          />
        </div>
      </HorizontalFormSection>
      {showDeleteProfileModal && (
        <DeleteAccountModal
          show={showDeleteProfileModal}
          close={closeDeleteProfileModal}
        />
      )}
      <ConfirmationModal
        title={translate('profileSettings.confirmationModal.title')}
        description={translate('profileSettings.confirmationModal.description')}
        show={showDeleteProfilePictureModal}
        close={closeDeleteProfilePictureModal}
        onConfirm={deleteProfilePicture}
      />
    </div>
  )
}
